import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TokenStorageService } from '../../services/token-storage.service';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfirmedValidator } from 'src/app/objects/confirmedValidator';
import { AccountDTO } from 'src/app/interfaces/account';
import { UserDTO } from 'src/app/interfaces/user';
import { AccountService } from 'src/app/services/account.service';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgIf, DatePipe } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { firstValueFrom } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserSubscriptionDTO } from 'src/app/interfaces/userSubscription';
import { UserSubscriptionService } from 'src/app/services/user-subscription.service';
import { StripeService } from 'src/app/services/stripe.service';
import { SubscriptionDTO } from 'src/app/interfaces/subscription';
import { SubscriptionService } from 'src/app/services/subscription.service';

@Component({
  selector: 'app-account',
  standalone: true,
  imports: [
    MatIconModule,
    MatDividerModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    NgIf,
    DatePipe,
    MatInputModule,
    MatButtonModule,
    MatTabsModule
  ],
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
})
export class AccountComponent implements OnInit {
  form: any = {
    email: '',
    currentpassword: '',
    newpassword: '',
    confirmpassword: ''
  };

  isLoggedIn = false;
  user: UserDTO;
  hide_current = true;
  hide_new = true;
  hide_confirm = true;

  accountForm: FormGroup;
  passwordForm: FormGroup;

  account: AccountDTO;
  proSubscriptionDTO: SubscriptionDTO;
  userSubscription: UserSubscriptionDTO;
  errorMessage: string;
  changeFailed: boolean = false;
  private snackBar = inject(MatSnackBar);
  selectedTabIndex = 0; // Standaard eerste tab (bijv. Profiel)
  isSubscriptionActive: boolean = false;
  
  // Mappen van tab-namen naar indexen
  private tabMap: { [key: string]: number } = {
    profile: 0,
    subscription: 1,
    settings: 2
  };

  constructor(
    private accountService: AccountService,
    private fb: FormBuilder,
    private tokenStorageService: TokenStorageService,
    private userSubscriptionService: UserSubscriptionService,
    private router: Router,
    private stripeService: StripeService,
    private subscriptionService: SubscriptionService,
    private route: ActivatedRoute
  ) {
    this.accountForm = this.fb.group({
      email: new FormControl('', [
        Validators.required,
        Validators.pattern("^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,4}$")
      ])
    });

    this.passwordForm = this.fb.group({
      currentpassword: new FormControl('', [Validators.required, Validators.minLength(6)]),
      newpassword: new FormControl('', [Validators.required, Validators.minLength(6)]),
      confirmpassword: new FormControl('', [Validators.required, Validators.minLength(6)])
    }, {
      validator: ConfirmedValidator('newpassword', 'confirmpassword')
    });
  }

  async ngOnInit() {
    this.isAuthorized();
    if (this.user) {
      this.form.email = this.user.email;
      this.accountForm.patchValue({ email: this.user.email });
      this.userSubscription = await firstValueFrom(this.userSubscriptionService.getUserSubscription());
      this.isSubscriptionActive = this.userSubscription.status === 'ACTIVE';
      this.proSubscriptionDTO = await firstValueFrom(this.subscriptionService.getSubscription("PRO"));
    }
    this.route.queryParams.subscribe(params => {
      const tabParam = params['tab'];

      // Als de parameter een bekende naam is (bijv. 'subscription')
      if (tabParam && this.tabMap[tabParam] !== undefined) {
        this.selectedTabIndex = this.tabMap[tabParam];
      }
      // Of als je direct een getal meegeeft (bijv. ?tab=1)
      else if (!isNaN(Number(tabParam))) {
        this.selectedTabIndex = Number(tabParam);
      }
    });
  }

  isAuthorized() {
    this.isLoggedIn = !!this.tokenStorageService.getToken();
    if (this.isLoggedIn) {
      this.user = this.tokenStorageService.getUser();
    } else {
      this.router.navigate(['home']);
    }
  }

  async saveEmail() {
    if (this.accountForm.invalid) return;
    this.user.email = this.accountForm.value.email;

    try {
      const updated = await firstValueFrom(this.accountService.updateAccount(this.account.id, { email: this.user.email } as AccountDTO));
      if (updated) {
        this.tokenStorageService.saveUser(this.user);
        this.snackBar.open('Email address updated successfully', 'OK', { duration: 3000 });
        this.accountForm.markAsPristine();
      }
    } catch (error) {
      this.snackBar.open('Failed to update email address', 'OK', { duration: 3000 });
    }
  }

  async savePassword() {
    if (this.passwordForm.invalid) return;
    this.changeFailed = false;

    const payload: AccountDTO = {
      email: this.user.email,
      changePassword: true,
      currentPassword: this.passwordForm.value.currentpassword,
      newPassword: this.passwordForm.value.newpassword
    } as AccountDTO;

    try {
      const data = await firstValueFrom(this.accountService.updateAccount(this.user.id, payload));
      if (data) {
        this.snackBar.open('Password changed successfully', 'OK', { duration: 3000 });
        this.passwordForm.reset();
      } else {
        this.changeFailed = true;
        this.errorMessage = 'Invalid current password';
      }
    } catch (error) {
      this.changeFailed = true;
      this.errorMessage = 'Failed to change password. Check your current password.';
    }
  }

  async buySubscription() {
    this.snackBar.open('Redirecting to billing portal...', 'OK', { duration: 2000 });
    const data = await firstValueFrom(this.stripeService.createCheckoutSession(this.user.id, this.proSubscriptionDTO.id));
    window.location.href = data.url;
  }

  async openStripePortal() {
    this.snackBar.open('Redirecting to billing portal...', 'OK', { duration: 2000});
    const data = await firstValueFrom(this.stripeService.createPortalSession(this.user.id));
    window.location.href = data.url;
  }

  get email() { return this.accountForm.get('email'); }
  get currentpassword() { return this.passwordForm.get('currentpassword'); }
  get newpassword() { return this.passwordForm.get('newpassword'); }
  get confirmpassword() { return this.passwordForm.get('confirmpassword'); }
}